import { useEffect, useState } from 'react';

import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { customInstanceApis } from '../../../apis/customInstanceApis';
import { instanceApis } from '../../../apis/instanceApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import SettingsTabs from '../../../components/page-sections/settings/SettingsTabs';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE, buildPath } from '../../../routes/routes';
import { sentenceCase } from '../../../utils/utils';

const CustomInstSettingsPage = () => {
  const navigate = useNavigate();
  const { projectId, customInstId } = useParams();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [error, setError] = useState(false);
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInstance = async () => {
    try {
      const { data: customInstRes } =
        await customInstanceApis.getCustomInstance(projectId, customInstId);

      setInstance(customInstRes);
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    }
  };

  useEffect(() => {
    if (!customInstId) return;
    const onPageLoad = async () => {
      setLoading(true);
      await fetchInstance();
      setLoading(false);
    };
    onPageLoad();
  }, []);

  const handleDeleteInstance = async () => {
    try {
      await customInstanceApis.deleteCustomInstance(projectId, customInstId);

      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('deleteSuccess', {
            ns: 'notifications',
            text: t('customInst', { ns: 'customInst' }),
          })
        )
      );
      const projectOverview = buildPath({
        path: PRIVATE_ROUTE.PROJECT_OVERVIEW,
        params: { projectId: projectId },
      });
      setTimeout(() => navigate(projectOverview), 800);
    } catch (errors) {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('deleteError', {
            ns: 'notifications',
            text: t('customInst', { ns: 'customInst' }),
          })
        )
      );
    }
  };

  const handleRemoveImage = async () => {
    await instanceApis.deleteInstanceImage(projectId, customInstId);
    await fetchInstance();
  };

  const handleUpdateInstance = async (data) => {
    const { icon, ...form } = data;
    // The custom instance needs to include the designation.
    const formattedForm = { ...form, designation: instance.designation };

    if (data.icon) {
      const formData = new FormData();
      formData.append('image', icon);
      await instanceApis.updateInstanceImage(projectId, customInstId, formData);
    }

    await customInstanceApis.updateCustomInstance(
      projectId,
      customInstId,
      formattedForm
    );
    await fetchInstance();
  };

  if (loading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={t('fetch', {
          ns: 'errors',
          data: t('instance', { ns: 'instance' }),
        })}
      />
    );

  return (
    <Container>
      <Breadcrumbs customInstName={instance.name} />
      <TitlePanel title={t('settings', { ns: 'titles' })} />
      <SettingsTabs
        instance={instance}
        onDeleteInstance={handleDeleteInstance}
        onRemoveImage={handleRemoveImage}
        onUpdateInstance={handleUpdateInstance}
      />
    </Container>
  );
};

export default CustomInstSettingsPage;
